import { metaAdapter } from "@meta/index";
import { MetaZipCodeContext } from "@meta/types";
import { capitalize, resizeVtexImage, toItemUrl } from "@core/utils";
import { normalizeBrand } from "@core/farm";
import { 
  getItemCategory,
  getItemCategory2,
  getOrderFormItems,
  getPaymentType,
  getItemShippingTier,
} from './utils'
import type { EventContext, InitialConfig, ItemsResponse, ShippingsResponse } from "@src/types";
import type {
  OFBrandContext,
  OFFlagPickupContext,
  OFItemsContext,
  OFLineItemsContext,
  OFShippingsContext,
  OFTotalDiscountContext,
  OFValueContext,
  OrderFormItem,
} from "./types";

export const orderFormAdapter = {
  ...metaAdapter,

  items: (context: OFItemsContext): ItemsResponse[] | null => {
    if (context?.items) return context.items
    
    if (context?.orderForm && context?.orderForm.items) {
      return getOrderFormItems(context.orderForm).map((item: OrderFormItem, index) => {
        // TODO REMOVE THIS AFTER CAROL BASSI UPDATES VTEX ADMIN
        const color = context.brand === 'carolbassi' ? null : item?.skuName?.split(' - ')[0]?.split('_').pop()?.trim();
        const size = context.brand === 'carolbassi' ? item?.skuName : item?.skuName?.split(' - ')[1]?.trim();

        return {
          quantity: item?.quantity ?? 1,
          index: context?.orderForm?.index ?? index + 1,
          item_brand: normalizeBrand(item.additionalInfo?.brandName) ?? capitalize(item.additionalInfo?.brandName), // farm mantém "farmrio"/"farmetc"; demais marcas capitalizadas
          item_name: item?.name.replace(item?.skuName, '').trim() ?? null,
          price: item?.sellingPrice / 100,
          discount: (item?.listPrice - item?.sellingPrice) / 100,
          item_variant: color ?? null,
          item_variant2: size?.toUpperCase() ?? null,
          item_shipping_tier: getItemShippingTier(context.orderForm, item),
          item_ref: item?.productRefId || item?.refId.replace(/_\d+$/, "") || null,
          item_category: getItemCategory(item) ?? null,
          item_category2: getItemCategory2(item) ?? null,
          item_id: item?.productId ?? null,
          item_sku: item?.id ?? null,
          item_url: toItemUrl(item.detailUrl),
          image_url: resizeVtexImage(item.imageUrl) ?? null,
          seller_id: item?.seller ?? null,
          item_list_name: context?.orderForm?.item_list_name ?? null,
        }
      })
    }

    return null
  },

  brand: (context: OFBrandContext, config: InitialConfig): string | null => {
    if (config?.brand) return config.brand
    if (context?.brand) return context.brand

    if (!context?.orderForm?.items) return null;

    const brands = getOrderFormItems(context.orderForm).map(item => item.additionalInfo.brandName);
    return normalizeBrand(brands)
  },

  line_items: (context: OFLineItemsContext): string | null => {
    if (context?.line_items) return context.line_items;
    if (!context?.orderForm?.items) return null;

    return getOrderFormItems(context.orderForm)
      .map((item) => item?.productId)
      .filter((id): id is string => Boolean(id))
      .join()
  },

  value: (context: OFValueContext): number | null => {
    if (context?.value) return context.value;
    if (!context?.orderForm?.items) return null;

    return getOrderFormItems(context.orderForm)
      .map((item) => (item?.sellingPrice / 100) * item.quantity)
      .filter((price): price is number => price !== undefined)
      .reduce((sum, price) => sum + price, 0);
  },

  payment_type: getPaymentType,

  total_discount: (context: OFTotalDiscountContext): number | null => {
    if (context?.total_discount) return context.total_discount;
    const orderForm = context?.orderForm;
    const items = orderForm?.items;
    if (!items?.length || typeof orderForm?.value !== "number") return null;

    const itemsFullValue = items.reduce((acc, item) => acc + item.listPrice * item.quantity, 0);
    const shipping = orderForm?.totalizers?.find((t) => t.id === "Shipping")?.value ?? 0;
    // desconto = (itens cheios + frete) - valor final pago
    const derived = itemsFullValue + shipping - orderForm.value;

    return derived > 0 ? derived / 100 : null;
  },

  subtotal: (context: EventContext) => {
    const totalsList = context.orderForm?.totalizers || context.orderForm?.totals || []; // totals for ordergroup and orderform have different names

    const subtotal =
      totalsList
        .filter((t: EventContext) => t.id === 'Items')
        .reduce((acc: number, t: EventContext) => acc + (t.value || 0), 0) / 100 || 0;

    return subtotal
  },

  zipcode: (context: MetaZipCodeContext) => {
    return context.meta.zipcode ?? null;
  },

  flag_pickup: (context: OFFlagPickupContext): boolean | null => {
    const orderForm = context.orderForm;
    if (!orderForm) return null;

    const logisticsInfo = orderForm.shippingData?.logisticsInfo || [];
    const hasPickup = logisticsInfo.some((info) =>
      info.slas?.some((sla) => sla.deliveryChannel === 'pickup-in-point')
    );

    return hasPickup
  },

  shippings: (context: OFShippingsContext): ShippingsResponse[] | null => {
    if (!context.orderForm) return null;
    const { items, shippingData } = context.orderForm;
    const acc: Record<string, any> = {};

    shippingData.logisticsInfo.forEach(({ itemIndex, slas }) => {
      const item = items[itemIndex];
      if (!item) return;
      slas.forEach((sla) => {
        if (!acc[sla.name]) acc[sla.name] = {
          shipping_tier: sla.name,
          delivery_time: parseInt(sla.shippingEstimate),
          shipping: parseFloat((sla.price / 100).toFixed(1)),
          line_items: []
        };
        acc[sla.name].line_items.push(item.id);
      });
    });

    return Object.values(acc).map(s => ({ ...s, line_items: s.line_items.join(',') }));
  },

  shipping: (context: EventContext) => {
    const logistics = context?.orderForm?.shippingData?.logisticsInfo ?? []
    return logistics.reduce((total: number, item: any) => {
      const selected = item.slas?.find((sla: any) => sla.id === item.selectedSla)
      return total + (selected?.price ?? 0)
    }, 0) / 100
  },

  shipping_tier: (context: EventContext) => {
    const logistics = context?.orderForm?.shippingData?.logisticsInfo ?? []
    const selectedSlas = logistics
      .map((item: {
        selectedSla?: string,
        slas?: Array<{
          id: string,
          deliveryChannel: string,
          name?: string,
          pickupStoreInfo?: { friendlyName?: string }
        }>
      }) => {
        if (!item.selectedSla) return null;
        return item.slas?.find((sla) => sla.id === item.selectedSla) || null;
      })
      .filter(Boolean) as Array<{
        deliveryChannel: string,
        name?: string,
        pickupStoreInfo?: { friendlyName?: string }
      }>;

    if (selectedSlas.length === 0) return null;

    const uniqueTiers = Array.from(
      new Map(
        selectedSlas.map((sla) => {
          const key = sla.deliveryChannel === 'pickup-in-point'
            ? `pickup:${sla.pickupStoreInfo?.friendlyName || sla.name || ''}`
            : `delivery:${sla.name || ''}`;
          return [key, sla];
        })
      ).values()
    );

    const sortedTiers = uniqueTiers.sort((a, b) => {
      const aOrder = a.deliveryChannel === 'pickup-in-point' ? 0 : 1;
      const bOrder = b.deliveryChannel === 'pickup-in-point' ? 0 : 1;
      if (aOrder !== bOrder) return aOrder - bOrder;

      const aLabel = a.deliveryChannel === 'pickup-in-point'
        ? (a.pickupStoreInfo?.friendlyName || a.name || '')
        : (a.name || '');
      const bLabel = b.deliveryChannel === 'pickup-in-point'
        ? (b.pickupStoreInfo?.friendlyName || b.name || '')
        : (b.name || '');

      return aLabel.localeCompare(bLabel, 'pt-BR');
    });

    const shippingTier = sortedTiers
      .map((sla) => (
        sla.deliveryChannel === 'pickup-in-point'
          ? `retirada em loja${sla.pickupStoreInfo?.friendlyName ? `: ${sla.pickupStoreInfo.friendlyName}` : ''}`
          : sla.deliveryChannel === 'delivery'
            ? `receba em casa${sla.name ? `: ${sla.name}` : ''}`
            : null
      ))
      .filter(Boolean)
      .join(', ');

    return shippingTier || null;
  },

  transaction_id: (context: EventContext) => {
    return context.orderForm?.orderGroup?.toString() || null;
  },

  coupon_message: (context: EventContext) => {
    if (context.couponMessage) return context.couponMessage;

    const messages = context.orderForm?.messages || [];
    if (messages.length === 0) return 'Código de desconto aplicado com sucesso';

    const couponMessages = messages.filter((msg: EventContext) => msg.text.toLowerCase().includes('cupom'));
    if (couponMessages.length === 0) return 'Código de desconto aplicado com sucesso';

    return couponMessages.map((msg: EventContext) => msg.text).join(' | ');
  },

  coupon: (context: EventContext) => {
    return context.appliedCoupon || null;
  },

  seller_cod_name: (context: EventContext) => {
    return context.appliedSellerCodName || null;
  },
};